// pure
import '../ui/PageStateShare.scss';
import { Dialog } from '@shared/ui-react/Dialog';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { PageStateService } from '@shared/user-state/PageStateService';
import { cs } from '@shared/utils/utils-classname';
// libs
import * as React from 'react';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';

export const MapStatePartialLoad = {
    open: (itemUuid: string, onClickLoad: (state: IPageState) => void) => {
        ReactUtils.safeRender(<PageStateSelectiveLoadDialog itemUuid={itemUuid} onClickLoad={onClickLoad} />, document.getElementById('react-portal-root'));
    },

    close: () => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
    },
};

@observer
class PageStateSelectiveLoadDialog extends React.Component<{
    itemUuid: string;
    onClickLoad: (state: IPageState) => void;
}> {
    @observable isFetching = false;
    @observable item: IPageState = null;
    @observable checkedSections = {
        mapView: true,
        vehicles: true,
        models: true,
        logpoints: true,
        images: true,
        documents: true,
        schedule: true,
    };

    constructor(props: { itemUuid: string; onClickLoad: (state: IPageState) => void }) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        PageStateService.fetchItem(this.props.itemUuid).then(state => {
            this.item = state;
        });
    }

    render() {
        return (
            <Dialog title={this.item ? this.item.name : ''} onClose={MapStatePartialLoad.close}>
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 100,
                        zIndex: 0,
                        opacity: 0.7,
                        filter: 'blur(2px)',
                        backgroundImage: 'linear-gradient(-45deg, #F5F6F7 250px, transparent), url("' + (this.item ? this.item.thumbnail : null) + '")',
                        backgroundSize: 'cover',
                    }}
                />

                <div className={cs('padded', { 'overlay-loading': this.isFetching })} style={{ position: 'relative', zIndex: 1 }}>
                    <div className='text-center desc'>{kuura.getMessage('js_saved_views.partial_load_title')}</div>

                    <hr />
                    {this.item && <DialogBody item={this.item} checkedSections={this.checkedSections} />}
                </div>

                <footer className='text-center padded' style={{ position: 'relative', zIndex: 2, backgroundColor: 'white' }}>
                    <button onClick={MapStatePartialLoad.close}>{kuura.getMessage('general.close')}</button>
                    <button className='ik-button-primary' style={{ marginLeft: '1em' }} onClick={this.handleClickLoad}>
                        {kuura.getMessage('js_saved_views.load_selected')}
                    </button>
                </footer>
            </Dialog>
        );
    }

    handleClickLoad = () => {
        const state: IMapPageState = JSON.parse(JSON.stringify(this.item));

        state.data.panel.activePanel = null;

        if (!this.checkedSections.mapView) {
            delete state.data.map;
            delete state.data.activeAlignmentsId;
        }

        if (!this.checkedSections.vehicles) {
            delete state.data.panel.vehicles;
        }

        if (!this.checkedSections.models) {
            delete state.data.panel.models;
            delete state.data.panel.bgMaps;
        }

        if (!this.checkedSections.logpoints) {
            delete state.data.panel.logpoints;
        }

        if (!this.checkedSections.images) {
            delete state.data.panel.images;
        }

        if (!this.checkedSections.documents) {
            delete state.data.panel.documents;
        }

        this.props.onClickLoad(state);
        setTimeout(() => {
            MapStatePartialLoad.close();
        }, 200);
    };
}

@observer
class DialogBody extends React.Component<{
    item: IPageState;
    checkedSections: { [key: string]: boolean };
}> {
    setAll = (state: boolean) => {
        Object.keys(this.props.checkedSections).forEach(key => {
            this.props.checkedSections[key] = state;
        });
    };

    toggleProp = (prop: string) => {
        this.props.checkedSections[prop] = !this.props.checkedSections[prop];
    };

    isAllChecked = (): boolean => {
        return Object.values(this.props.checkedSections).every(val => !!val);
    };

    render() {
        const { item, checkedSections } = this.props;
        const counts = (item as IMapPageState).data.featureCounts;

        return (
            <div className='card' style={{ marginLeft: 200 }}>
                <div className='flex-row' style={{ alignItems: 'end' }}>
                    <div style={{ borderLeft: '1px solid #e8e8e8', paddingLeft: 10 }}>
                        <table className='simple-table'>
                            <thead>
                                <tr>
                                    <th style={{ width: 30 }}>
                                        <input type='checkbox' onChange={e => this.setAll(e.currentTarget.checked)} checked={this.isAllChecked()} />
                                    </th>
                                    <th style={{ width: 200 }}>{kuura.getMessage('js_saved_views.panel')}</th>
                                    <th style={{ width: 100 }}>{kuura.getMessage('js_saved_views.count')} *</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        <input type='checkbox' checked={checkedSections.mapView} onChange={() => this.toggleProp('mapView')} />
                                    </td>
                                    <td>{kuura.getMessage('js_saved_views.map_view')}</td>
                                    <td>/</td>
                                </tr>

                                <tr>
                                    <td>
                                        <input type='checkbox' checked={checkedSections.vehicles} onChange={() => this.toggleProp('vehicles')} />
                                    </td>
                                    <td>{kuura.getMessage('menu.vehicles')}</td>
                                    <td>/</td>
                                </tr>

                                <tr>
                                    <td>
                                        <input type='checkbox' checked={checkedSections.models} onChange={() => this.toggleProp('models')} />
                                    </td>
                                    <td>{kuura.getMessage('menu.designs')}</td>
                                    <td>{counts.models}</td>
                                </tr>

                                <tr>
                                    <td>
                                        <input type='checkbox' checked={checkedSections.logpoints} onChange={() => this.toggleProp('logpoints')} />
                                    </td>
                                    <td>{kuura.getMessage('general.logpoints')}</td>
                                    <td>{counts.logpoints}</td>
                                </tr>

                                <tr>
                                    <td>
                                        <input type='checkbox' checked={checkedSections.documents} onChange={() => this.toggleProp('documents')} />
                                    </td>
                                    <td>{kuura.getMessage('general.documents')}</td>
                                    <td>{counts.documents}</td>
                                </tr>

                                <tr>
                                    <td>
                                        <input type='checkbox' checked={checkedSections.images} onChange={() => this.toggleProp('images')} />
                                    </td>
                                    <td>{kuura.getMessage('menu.images')}</td>
                                    <td>{counts.images}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }
}
